/**
 * Home, for somebody who has already played. A first visit never gets here --
 * it lands on `FirstRunScreen` -- which is what finally let this screen stop
 * trying to be a tutorial and a launcher at the same time.
 *
 * IT GREW BACK INTO A FORM. TWICE.
 *
 * The target has always been "playable in under 15 seconds with no account",
 * and the file said so, and it was fought for once already: course was
 * demoted, the tiers were labelled Easiest/Standard/Hardest, `HowToPlay` was
 * added to define the nouns. Then three game modes arrived, then group
 * matches, then duel links, then accounts -- and a returning player faced
 * fifteen controls, six of which started something, before anything had been
 * asked of them. Playtesters said they spent the first screen working out what
 * they were even choosing.
 *
 * The answer was not a shorter list. It was an ORDER. This screen asks one
 * thing; `PlayScreen` asks the next. Everything that used to sit here -- the
 * modes, the difficulty, the courses, the daily -- lives there, on a screen
 * reached by somebody who has already said they want to play, with the numbers
 * printed on it so the choice is informed rather than guessed.
 *
 * That costs a regular one extra tap, which is exactly what changing mode
 * already cost. What it buys is that nothing here is a question.
 *
 * WHAT STAYS. Playing with friends, because it is the reason anybody tells
 * anybody else about this. `More`, holding the things you go looking for
 * rather than the things you are offered. And the account, which is the one
 * thing worth interrupting a regular about -- without it their progress lives
 * in a single browser and dies with it.
 */

import { useEffect, useRef, useState } from 'react';
import { HANDLE_MAX_LENGTH } from '@geogame/shared';
import type { GameMode } from '@geogame/shared';

import { AccountPanel } from '../components/AccountPanel';
import { HowToPlay } from '../components/HowToPlay';
import { useEntrance } from '../lib/entrance';
import { navigate } from '../lib/router';
import { useGame } from '../store/gameStore';
import { hasPlayedBefore } from '../store/storage';
import { FriendDuelPanel } from './FriendDuelPanel';

export function HomeScreen(): React.JSX.Element {
  const startMatch = useGame((s) => s.startMatch);
  const tier = useGame((s) => s.tier);
  const handle = useGame((s) => s.handle);
  const setHandle = useGame((s) => s.setHandle);

  /**
   * Read ONCE on mount. `startMatch` marks this browser as having played, so
   * reading it live would make the guided-round card vanish under the player's
   * thumb on the way back from their first match.
   */
  const [firstTime] = useState(() => !hasPlayedBefore());

  const entering = useEntrance();

  /*
   * Shut by default and never persisted: the default state of this screen
   * should be the same every time, because a screen that remembers which
   * panels were open is a screen whose shape a player cannot learn.
   */
  const [friendOpen, setFriendOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  /**
   * THE NAME BOX SAVES WHEN YOU SAY SO.
   *
   * It used to write on every keystroke and flash the word "saved" beside the
   * heading for a second and a half. That was true and it was useless: the
   * only moment the confirmation existed was the moment the player was looking
   * at their own thumb. Reported twice -- "saving your name is not intuitive",
   * then "how do I know if I change my name that it saves".
   *
   * So there is a button, and it appears exactly when there is something to
   * save. A control that shows up when you have made a change and leaves when
   * the change is stored is a status display that happens to be pressable, and
   * it answers the question without anybody having to read a note.
   *
   * SAFE TO IGNORE, WHICH IS THE POINT OF THE BLUR. A button nobody presses
   * would otherwise be a name nobody keeps -- somebody types, taps Play, and
   * finds themselves called something else. Leaving the field commits it too.
   *
   * This is only safe because nothing reads the handle between keystrokes:
   * `startMatch` is the sole consumer. The LOBBY's name field is the opposite
   * case and stays live per keystroke, because there it renames you on
   * everybody else's roster while they watch.
   */
  const [draft, setDraft] = useState(handle);
  const dirty = draft !== handle;

  /*
   * THE NAME CAN CHANGE WITHOUT ANYBODY TYPING.
   *
   * A browser that has never been named arrives with an empty handle and is
   * given one by `welcome` a second later. `draft` is seeded once at mount, so
   * without this it would still hold the empty string while the store held
   * `PolarFjord417` -- and the field would sit there offering to Save a name
   * the player had not touched, which is precisely the confusion this whole
   * change is meant to remove.
   *
   * Adopted ONLY when the draft still matches what the store last said. If
   * they have typed something of their own, that wins: a rename arriving from
   * elsewhere must never overwrite half a word somebody is in the middle of.
   */
  const lastStored = useRef(handle);
  useEffect(() => {
    if (handle === lastStored.current) return;
    if (draft === lastStored.current) setDraft(handle);
    lastStored.current = handle;
  }, [handle, draft]);

  const commitName = (): void => {
    if (!dirty) return;
    setHandle(draft);
  };

  /**
   * What a friend link is minted with.
   *
   * COUNTRIES AND WHATEVER TIER THIS BROWSER LAST PLAYED, and it matters much
   * less than it used to: the lobby is now the authority on what it will play,
   * and the host changes it from inside with everybody watching. The link's
   * settings are a starting point rather than a commitment.
   */
  const linkMode: GameMode = 'country';

  return (
    <main className="gg-screen gg-scroll" data-entering={entering ? 'true' : undefined}>
      <header className="gg-hero">
        <h1 className="gg-title">Worldly</h1>
        <p className="gg-subtitle">
          Find the country on the globe. Then keep what you won, or bet it on a question.
        </p>
      </header>

      {/*
        The rules stay above the fold for somebody who skipped the intro, and
        move into `More` for everybody else. A returning player has met every
        noun on this screen; a skipper has met none of them.
      */}
      {firstTime ? <HowToPlay /> : null}

      {/*
        Loud only for somebody who has never started a match -- which, now that
        a first visit lands on `FirstRunScreen`, means somebody who SKIPPED the
        intro. They said "not now", not "never", so the offer stays; it just
        stops being the first thing on the screen once they have played.
      */}
      {firstTime ? (
        <section className="gg-section">
          <button
            type="button"
            className="gg-guided"
            onClick={() => startMatch({ mode: 'practice', tier, coached: true })}
          >
            <span className="gg-guided-kicker">First time here?</span>
            <span className="gg-guided-title">Play a guided round</span>
            <span className="gg-guided-sub">
              One offline match against nobody, with a line of coaching at each step and a relaxed
              clock on the first round. No opponent, no ranking, nothing to lose.
            </span>
          </button>
        </section>
      ) : null}

      <section className="gg-section gg-actions">
        <button
          type="button"
          className="gg-btn gg-btn-primary gg-btn-xl gg-play"
          onClick={() => navigate({ name: 'play' })}
        >
          <span className="gg-play-label">Play</span>
        </button>

        {/*
          STRAIGHT INTO THE LOBBY. Pressing this used to reveal a panel whose
          only content was a button that said "Open a lobby" -- the tap that
          opened the submenu and the tap inside it were the same intention
          expressed twice. Mounting the panel now mints the link and goes.
        */}
        <button
          type="button"
          className="gg-btn"
          disabled={friendOpen}
          onClick={() => setFriendOpen(true)}
        >
          Play with friends
          <span className="gg-alt-note">
            {friendOpen ? 'opening…' : 'up to six on one link'}
          </span>
        </button>

        <button
          type="button"
          className="gg-btn"
          aria-expanded={moreOpen}
          onClick={() => setMoreOpen((v) => !v)}
        >
          More
          <span className="gg-alt-note">{moreOpen ? 'close' : 'progress, rules'}</span>
        </button>
      </section>

      {friendOpen ? <FriendDuelPanel tier={tier} gameMode={linkMode} courseId="" /> : null}

      {moreOpen ? (
        <>
          <section className="gg-section">
            <div className="gg-alt-row">
              <button
                type="button"
                className="gg-btn"
                onClick={() => startMatch({ mode: 'practice', tier })}
              >
                Practice
                <span className="gg-alt-note">offline</span>
              </button>
              <button
                type="button"
                className="gg-btn"
                onClick={() => navigate({ name: 'progress' })}
              >
                Progress
                <span className="gg-alt-note">what you know</span>
              </button>
              <button type="button" className="gg-btn" onClick={() => navigate({ name: 'play' })}>
                Modes
                <span className="gg-alt-note">and difficulty</span>
              </button>
            </div>
          </section>

          {/*
            THE GUIDED ROUND HAS TO STAY REACHABLE.
            ================================================================
            It used to exist only inside a `firstTime` branch read once at
            mount, which meant a player who bounced during their first match --
            the exact person who most needed it -- had permanently lost the
            only explanation the game offers. There is no account to reset and
            no menu to find it in. It is one level quieter than it was, behind
            `More`, and still always here.
          */}
          {!firstTime ? (
            <section className="gg-section gg-section-quiet">
              <button
                type="button"
                className="gg-btn"
                onClick={() => startMatch({ mode: 'practice', tier, coached: true })}
              >
                Replay the guided round
              </button>
            </section>
          ) : null}

          {!firstTime ? <HowToPlay /> : null}

          <section className="gg-section gg-section-quiet">
            <h2 className="gg-section-subtitle">Your name</h2>
            <div className="gg-name-row">
              <input
                className="gg-input"
                value={draft}
                maxLength={HANDLE_MAX_LENGTH}
                // Clearing the box is a legal state, not an error: leave it
                // empty and the gateway names you. Which is why there is a
                // placeholder here and no minimum length anywhere on the path.
                placeholder="Leave blank and we'll name you"
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitName();
                  }
                }}
                aria-label="Display name"
              />
              {dirty ? (
                <button type="button" className="gg-btn gg-btn-primary" onClick={commitName}>
                  Save
                </button>
              ) : (
                <span className="gg-name-saved">Saved</span>
              )}
            </div>
            <p className="gg-note">
              Only what other players see. There is no account or email behind it.
            </p>
          </section>
        </>
      ) : null}

      {/*
        ACCOUNT DISCLOSES ITSELF, so this is one control and not four.
        `AccountPanel` is collapsed until asked: what shows here is a sentence
        saying why an account is worth having and one button. The field, the
        send and the Google button arrive on tap.

        It stays OUT of `More` deliberately. It is the one thing on this screen
        worth interrupting a regular about, because without it their progress
        lives in a single browser and dies with it.
      */}
      <AccountPanel />

      {/*
        The quietest thing on the screen, and the last. It exists so the
        policies are reachable by somebody looking for them rather than only by
        somebody Google sent.
      */}
      <footer className="gg-footer">
        <button type="button" onClick={() => navigate({ name: 'privacy' })}>
          Privacy
        </button>
        <span aria-hidden="true">·</span>
        <button type="button" onClick={() => navigate({ name: 'terms' })}>
          Terms
        </button>
      </footer>
    </main>
  );
}
